import set from 'date-fns/set';
import { Children, cloneElement, isValidElement, PropsWithChildren, useState } from 'react';
import styled, { css } from 'styled-components';

import { common } from 'app/translations/dictionary/common';
import { useClickOutside } from 'pkg/clickOutsideHook/clickOutsideHook';

interface DateTimePickerProps {
  date: Date | null;
  setDate: (date: Date) => void;
  onReject: () => void;
  column?: boolean;
  modalRef?: React.RefObject<HTMLDivElement>;
}

interface IsColumn {
  $column: boolean;
}

const ButtonsContainer = styled.div`
  position: static;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Divider = styled.div`
  border-bottom: 1px solid ${({ theme }) => `${theme.palette.gray.dividerGray}`};
  border-left: 1px solid ${({ theme }) => `${theme.palette.gray.dividerGray}`};
  margin: 0 -1.5rem;
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.palette.primary};
  cursor: pointer;
  font-weight: 600;

  &&&&:first-of-type {
    margin-right: 7rem;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const ItemContainer = styled.div`
  margin: 3rem 0;
  flex-grow: 1;

  &:first-of-type {
    margin: 0 0 3rem 0;
  }
`;

const MobileDivider = styled(Divider)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.gray.dividerGray};
  border-left: 1px solid ${({ theme }) => theme.palette.gray.dividerGray};
  margin: 0 -1.5rem 1.5rem -1.5rem;
`;

const DatePickerContainer = styled.div<IsColumn>`
  position: relative;
  display: flex;
  min-height: 32rem;
  min-width: 0;
  background-color: ${({ theme }) => theme.palette.white};
  padding: 2.5rem 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 0.7rem 1.5rem rgba(0, 0, 0, 0.35);
  flex-direction: column;

  ${({ $column }) =>
    !$column &&
    css`
      margin: 0;
      flex-direction: row;
      min-width: 45rem;

      ${ButtonsContainer} {
        width: auto;
        display: block;
        position: absolute;
        bottom: 2.5rem;
        right: 1.8rem;
      }

      ${Divider} {
        margin: -2.5rem 0;
      }

      ${ActionButton}:first-of-type {
        margin-right: 5.3rem;
      }

      ${ItemContainer} {
        margin: 0 0 0 1.8rem;

        &:first-of-type {
          margin: 0 3rem 0 0;
        }
      }
    `}
`;

export const DateTimePicker = ({
  children,
  date,
  setDate,
  onReject,
  column = true,
  modalRef,
}: PropsWithChildren<DateTimePickerProps>): JSX.Element => {
  const initialDate = date ?? new Date();
  const [internalDate, setInternalDate] = useState(initialDate);

  const handleReject = () => {
    setInternalDate(initialDate);
    onReject();
  };

  const handleAccept = () => {
    setDate(set(internalDate, { milliseconds: 0, seconds: 0 }));
  };

  useClickOutside(modalRef?.current, handleAccept);

  return (
    <DatePickerContainer $column={column}>
      {Children.map(children, (picker, index) => {
        if (isValidElement(picker)) {
          return (
            <>
              <ItemContainer>{cloneElement(picker, { date: internalDate, setDate: setInternalDate })}</ItemContainer>
              {index <= Children.count(children) - 2 && <Divider />}
            </>
          );
        }
        return null;
      })}
      {column && <MobileDivider />}
      <ButtonsContainer>
        <ActionButton onClick={handleReject}>{common.cancel}</ActionButton>
        <ActionButton onClick={handleAccept}>{common.ok}</ActionButton>
      </ButtonsContainer>
    </DatePickerContainer>
  );
};
